import React, { useState } from 'react';
import { Info, AlertTriangle, CheckCircle2, RotateCcw } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTES QUALITÉ · M8 (Marche arrière) · M9 (Demi-tour) · M11 (Démarrage côte)
// Composant unique paramétré par `stepId` pour éviter la duplication de code.
// ══════════════════════════════════════════════════════════════════════════

const CONFIGS = {
  M8: {
    title: 'M8 · Marche arrière (ligne droite + courbe)',
    remc: 'C1i',
    modes: [
      { id: 'ligne', label: 'Ligne droite', color: '#22c55e' },
      { id: 'courbe', label: 'En courbe', color: '#f59e0b' },
    ],
    contenu: {
      ligne: {
        titre: "MA en ligne droite",
        description: "Regard TOURNÉ vers l'arrière (par-dessus l'épaule droite), main gauche seule sur le volant en haut, main droite sur le dossier passager pour bien tourner le buste. Levier en R.",
        etapes: ["Débrayer + R", "Regard 100% arrière", "Embrayage progressif SANS gaz", "Petites corrections de volant"],
        piege: "Regarder les rétros à la place de tourner la tête. Vision réduite = angle mort géant.",
        geste: "Vitesse de marche à pied. Prêt à débrayer au moindre doute. Rétros = complément, PAS remplacement.",
      },
      courbe: {
        titre: "MA en courbe (créneau, contournement)",
        description: "Braquage volant AU MOMENT du recul. Vitesse ULTRA-lente au point d'embrayage. Regard alternant AR (75%) et intérieur du virage (25%).",
        etapes: ["Aligner devant le vide de stationnement", "Braquer FRANC dans le sens du recul", "Reculer TRÈS lentement", "Contre-braquer pour redresser"],
        piege: "Braquer AVANT de reculer = trajectoire ratée. Reculer trop vite = collision inévitable.",
        geste: "Un demi-tour de volant supplémentaire quand la roue arrière atteint le milieu du trottoir cible.",
      },
    },
  },
  M9: {
    title: 'M9 · Demi-tour',
    remc: 'C1i',
    modes: [
      { id: 'demi3temps', label: 'Demi-tour en 3 temps', color: '#8b5cf6' },
      { id: 'sortiepriv', label: 'Utiliser une sortie privée', color: '#0ea5e9' },
    ],
    contenu: {
      demi3temps: {
        titre: "Demi-tour en 3 temps (route étroite)",
        description: "Vérifier absence de circulation dans les 2 sens. 1er temps : avancer + braquer G à fond. 2ème : reculer + contre-braquer D à fond. 3ème : avancer + rectifier.",
        etapes: ["Contrôle rétros + clignotant G", "1er : cap 90° G (avant)", "2e : recul + volant D (aligner arrière)", "3e : redresser et repartir"],
        piege: "Faire un demi-tour à un endroit interdit (croisement, virage sans visibilité, ligne blanche continue).",
        geste: "Si la manœuvre nécessite plus de 3 temps → c'est OK. Ne JAMAIS forcer. La sécurité prime.",
      },
      sortiepriv: {
        titre: "Utiliser une sortie privée / parking",
        description: "Repérer une allée, un chemin ou un parking à droite. Y engager la marche arrière calmement, sortir en marche avant.",
        etapes: ["Repérer un accès à DROITE", "S'y engager en marche AVANT", "Sortir en marche ARRIÈRE contrôlée", "Reprendre la route"],
        piege: "Utiliser une sortie où la visibilité est nulle = collision quasi-garantie. Choisir une entrée dégagée.",
        geste: "Préférer TOUJOURS cette méthode au demi-tour 3 temps si l'occasion se présente : plus sûre, plus fluide.",
      },
    },
  },
  M11: {
    title: 'M11 · Démarrage en côte',
    remc: 'C1d',
    modes: [
      { id: 'freinamain', label: 'Avec frein à main', color: '#22c55e' },
      { id: 'sansfrein', label: 'Sans frein à main', color: '#dc2626' },
    ],
    contenu: {
      freinamain: {
        titre: "Démarrage en côte AVEC frein à main",
        description: "Méthode officielle examen. Frein à main serré. Débrayer, 1re, un peu de gaz, monter au point de patinage, PUIS relâcher frein à main progressivement.",
        etapes: ["Frein à main SERRÉ", "Débrayer + 1re", "Gaz doux (~1500 tr/min)", "Trouver le point de patinage", "Relâcher frein à main + accélérer"],
        piege: "Relâcher le frein à main AVANT le point de patinage = la voiture recule.",
        geste: "La voiture ne DOIT PAS reculer d'un cm. Si tu recules → le point de patinage n'était pas atteint.",
      },
      sansfrein: {
        titre: "Démarrage en côte SANS frein à main (technique avancée)",
        description: "Pour côtes très légères. Pied du frein → pied du gaz avec bascule ultra rapide, pendant que le pied gauche relâche l'embrayage au patinage.",
        etapes: ["Pied G au point de patinage", "Bascule frein → gaz du pied D (< 0.3 s)", "Poursuivre relâche embrayage"],
        piege: "Trop lent à la bascule = tu recules. À éviter en côte forte. Réservé aux conducteurs confirmés.",
        geste: "S'entraîner en pente légère (2-4 %) avant tout autre. Une côte réelle en agglo peut se faire ainsi si tu es à l'aise.",
      },
    },
  },
};

export default function ManoeuvresSvg({ stepId = 'M8' }) {
  const cfg = CONFIGS[stepId] || CONFIGS.M8;
  const [mode, setMode] = useState(cfg.modes[0].id);
  const [sim, setSim] = useState('idle'); // idle | success | fail (utilisé pour M11)
  const current = cfg.contenu[mode];
  const currentMode = cfg.modes.find((m) => m.id === mode);

  const runSim = (outcome) => {
    setSim('idle');
    // Petit délai pour forcer le remount des <animateTransform> et redémarrer l'animation
    setTimeout(() => setSim(outcome), 60);
  };

  return (
    <div className="w-full">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full border border-white/15 overflow-hidden">
          {cfg.modes.map((m, i) => (
            <button key={m.id} type="button" onClick={() => { setMode(m.id); setSim('idle'); }}
              data-testid={`${stepId.toLowerCase()}-mode-${m.id}`}
              className={`h-9 px-3.5 text-[11.5px] font-bold font-mono-spec ${mode === m.id ? 'text-white' : 'text-white/60'} ${i > 0 ? 'border-l border-white/15' : ''}`}
              style={mode === m.id ? { background: m.color + '25' } : {}}
            >
              {m.label}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setMode(cfg.modes[0].id)} data-testid={`${stepId.toLowerCase()}-reset`} className="ml-auto h-9 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[11.5px] font-semibold inline-flex items-center gap-1">
          <RotateCcw size={12} /> Reset
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#1a2434] to-[#0a1220]">
          <svg viewBox="0 0 800 700" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label={cfg.title}>
            <defs>
              <linearGradient id="mv-road" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#334155" />
                <stop offset="100%" stopColor="#1e293b" />
              </linearGradient>
              <linearGradient id="mv-grass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4d7c3a" />
                <stop offset="100%" stopColor="#365828" />
              </linearGradient>
            </defs>

            <rect width={800} height={700} fill="url(#mv-grass)" />

            {/* Illustration selon step + mode */}
            {stepId === 'M8' && (
              <g>
                {/* Trottoirs (bordures grises) */}
                <rect x={150} y={0} width={50} height={700} fill="#475569" />
                <rect x={600} y={0} width={50} height={700} fill="#475569" />
                {/* Chaussée (2 voies) */}
                <rect x={200} y={0} width={400} height={700} fill="url(#mv-road)" />
                {/* Marquage bord de chaussée (blancs) */}
                <rect x={200} y={0} width={5} height={700} fill="#e2e8f0" opacity={0.6} />
                <rect x={595} y={0} width={5} height={700} fill="#e2e8f0" opacity={0.6} />
                {/* Ligne MÉDIANE pointillée jaune à x=400 */}
                <line x1={400} y1={0} x2={400} y2={700} stroke="#fbbf24" strokeWidth={4} strokeDasharray="24 16" opacity={0.9} />
                {/* Bandeau zone verte (voie de droite = zone où on doit rester) */}
                <rect x={400} y={0} width={200} height={700} fill="#22c55e" opacity={0.06} />

                {/* Étiquettes */}
                <text x={300} y={50} textAnchor="middle" fill="#94a3b8" fontSize={11} fontFamily="monospace" fontWeight="bold">VOIE INVERSE (interdite)</text>
                <text x={500} y={50} textAnchor="middle" fill="#22c55e" fontSize={12} fontFamily="monospace" fontWeight="bold">VOIE DE DROITE (ma voie)</text>
                <text x={400} y={35} textAnchor="middle" fill="#fbbf24" fontSize={10} fontFamily="monospace" fontWeight="bold">↕ LIGNE MÉDIANE — NE PAS FRANCHIR ↕</text>
                <text x={175} y={640} textAnchor="middle" fill="#cbd5e1" fontSize={9} fontFamily="monospace" transform="rotate(-90 175 640)">TROTTOIR</text>
                <text x={625} y={640} textAnchor="middle" fill="#cbd5e1" fontSize={9} fontFamily="monospace" transform="rotate(-90 625 640)">TROTTOIR</text>

                {mode === 'ligne' && (
                  <>
                    {/* Repère visuel : ligne verticale centrée voie de droite */}
                    <line x1={500} y1={100} x2={500} y2={620} stroke="#22c55e" strokeDasharray="4 6" strokeWidth={1.5} opacity={0.35} />

                    {/* Voiture EGO — vue de dessus, orientée nez vers le HAUT (marche avant = up),
                        anime de haut en bas puis remonte = boucle de marche arrière */}
                    <g>
                      <animateTransform
                        attributeName="transform"
                        type="translate"
                        values="500,180; 500,560; 500,180"
                        keyTimes="0; 0.5; 1"
                        dur="7s"
                        repeatCount="indefinite"
                      />
                      {/* Ombre */}
                      <ellipse cx={0} cy={44} rx={26} ry={6} fill="#000" opacity={0.4} />
                      {/* Corps */}
                      <rect x={-22} y={-42} width={44} height={84} rx={12} fill="#dc2626" stroke="#0b1120" strokeWidth={2.5} />
                      {/* Pare-brise avant (haut) */}
                      <path d="M -18,-34 L 18,-34 L 15,-10 L -15,-10 Z" fill="#0ea5e9" opacity={0.55} />
                      {/* Lunette arrière (bas) */}
                      <path d="M -18,32 L 18,32 L 15,10 L -15,10 Z" fill="#0ea5e9" opacity={0.45} />
                      {/* Feux de recul (blancs) allumés à l'arrière = bas */}
                      <rect x={-18} y={38} width={7} height={5} fill="#fef3c7" />
                      <rect x={11} y={38} width={7} height={5} fill="#fef3c7" />
                      <circle cx={-14} cy={40.5} r={3} fill="#fef3c7" opacity={0.85}>
                        <animate attributeName="opacity" values="0.4;1;0.4" dur="0.9s" repeatCount="indefinite" />
                      </circle>
                      <circle cx={14} cy={40.5} r={3} fill="#fef3c7" opacity={0.85}>
                        <animate attributeName="opacity" values="0.4;1;0.4" dur="0.9s" repeatCount="indefinite" />
                      </circle>
                      {/* Feux avant (haut) */}
                      <rect x={-18} y={-43} width={7} height={4} fill="#fbbf24" opacity={0.4} />
                      <rect x={11} y={-43} width={7} height={4} fill="#fbbf24" opacity={0.4} />
                      {/* Toit / label MOI */}
                      <rect x={-14} y={-10} width={28} height={20} rx={3} fill="#7f1d1d" opacity={0.35} />
                      <text y={4} textAnchor="middle" fontSize={11} fontWeight="900" fill="#fff">MOI</text>
                      {/* Flèche RECUL (vers le bas) */}
                      <g transform="translate(45 0)">
                        <line x1={0} y1={-25} x2={0} y2={25} stroke="#f59e0b" strokeWidth={3} strokeLinecap="round" />
                        <polygon points="0,30 -7,18 7,18" fill="#f59e0b" />
                        <text x={12} y={5} fill="#f59e0b" fontSize={11} fontWeight="bold" fontFamily="monospace">RECUL</text>
                      </g>
                    </g>

                    {/* Bulle conseil */}
                    <g transform="translate(660 320)">
                      <rect x={-5} y={-32} width={140} height={80} rx={8} fill="#0f172a" stroke="#22c55e" strokeWidth={1.5} opacity={0.9} />
                      <text x={65} y={-15} textAnchor="middle" fill="#22c55e" fontSize={10} fontFamily="monospace" fontWeight="bold">TRAJECTOIRE</text>
                      <text x={65} y={2} textAnchor="middle" fill="#e2e8f0" fontSize={10}>Reste parallèle</text>
                      <text x={65} y={16} textAnchor="middle" fill="#e2e8f0" fontSize={10}>à la médiane</text>
                      <text x={65} y={32} textAnchor="middle" fill="#fbbf24" fontSize={10} fontWeight="bold">sans la franchir</text>
                    </g>
                  </>
                )}

                {mode === 'courbe' && (
                  <>
                    {/* 2 voitures garées le long du trottoir droit (voie de droite = créneau) */}
                    <g transform="translate(555 130)">
                      <rect x={-22} y={-42} width={44} height={84} rx={12} fill="#3b82f6" stroke="#0b1120" strokeWidth={2} />
                      <path d="M -18,-34 L 18,-34 L 15,-10 L -15,-10 Z" fill="#0ea5e9" opacity={0.5} />
                      <path d="M -18,32 L 18,32 L 15,10 L -15,10 Z" fill="#0ea5e9" opacity={0.4} />
                    </g>
                    <g transform="translate(555 400)">
                      <rect x={-22} y={-42} width={44} height={84} rx={12} fill="#8b5cf6" stroke="#0b1120" strokeWidth={2} />
                      <path d="M -18,-34 L 18,-34 L 15,-10 L -15,-10 Z" fill="#0ea5e9" opacity={0.5} />
                      <path d="M -18,32 L 18,32 L 15,10 L -15,10 Z" fill="#0ea5e9" opacity={0.4} />
                    </g>
                    {/* Zone créneau cible entre les deux */}
                    <rect x={520} y={185} width={70} height={170} fill="none" stroke="#22c55e" strokeWidth={2.5} strokeDasharray="6 4" rx={8} />
                    <text x={555} y={280} textAnchor="middle" fill="#22c55e" fontSize={10} fontFamily="monospace" fontWeight="bold">CIBLE</text>
                    <text x={555} y={295} textAnchor="middle" fill="#22c55e" fontSize={10} fontFamily="monospace" fontWeight="bold">CRÉNEAU</text>

                    {/* Trajectoire de la voiture EGO (courbe pédagogique) */}
                    <path
                      d="M 470 620 L 470 470 Q 470 380 520 320 Q 555 285 555 270"
                      stroke="#f59e0b" strokeWidth={2.5} fill="none" strokeDasharray="8 5" opacity={0.7}
                    />

                    {/* Voiture EGO qui recule et effectue la courbe */}
                    <g>
                      <animateTransform
                        attributeName="transform"
                        type="translate"
                        values="470,620; 470,470; 520,320; 555,270; 555,270; 470,620"
                        keyTimes="0; 0.35; 0.65; 0.8; 0.9; 1"
                        dur="9s"
                        repeatCount="indefinite"
                      />
                      <g>
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          values="0; 0; -35; -55; -55; 0"
                          keyTimes="0; 0.35; 0.65; 0.8; 0.9; 1"
                          dur="9s"
                          repeatCount="indefinite"
                        />
                        <ellipse cx={0} cy={44} rx={26} ry={6} fill="#000" opacity={0.4} />
                        <rect x={-22} y={-42} width={44} height={84} rx={12} fill="#dc2626" stroke="#0b1120" strokeWidth={2.5} />
                        <path d="M -18,-34 L 18,-34 L 15,-10 L -15,-10 Z" fill="#0ea5e9" opacity={0.55} />
                        <path d="M -18,32 L 18,32 L 15,10 L -15,10 Z" fill="#0ea5e9" opacity={0.45} />
                        <circle cx={-14} cy={40.5} r={3} fill="#fef3c7">
                          <animate attributeName="opacity" values="0.4;1;0.4" dur="0.9s" repeatCount="indefinite" />
                        </circle>
                        <circle cx={14} cy={40.5} r={3} fill="#fef3c7">
                          <animate attributeName="opacity" values="0.4;1;0.4" dur="0.9s" repeatCount="indefinite" />
                        </circle>
                        <text y={4} textAnchor="middle" fontSize={11} fontWeight="900" fill="#fff">MOI</text>
                      </g>
                    </g>

                    {/* Numéros d'étape */}
                    <g fontFamily="monospace" fontSize={12} fontWeight="900">
                      <circle cx={430} cy={545} r={13} fill="#0f172a" stroke="#22c55e" strokeWidth={2} />
                      <text x={430} y={550} textAnchor="middle" fill="#22c55e">1</text>
                      <text x={385} y={550} textAnchor="end" fill="#22c55e" fontSize={9}>Aligner</text>

                      <circle cx={430} cy={430} r={13} fill="#0f172a" stroke="#f59e0b" strokeWidth={2} />
                      <text x={430} y={435} textAnchor="middle" fill="#f59e0b">2</text>
                      <text x={385} y={435} textAnchor="end" fill="#f59e0b" fontSize={9}>Reculer droit</text>

                      <circle cx={460} cy={340} r={13} fill="#0f172a" stroke="#8b5cf6" strokeWidth={2} />
                      <text x={460} y={345} textAnchor="middle" fill="#8b5cf6">3</text>
                      <text x={415} y={345} textAnchor="end" fill="#8b5cf6" fontSize={9}>Braquer D</text>

                      <circle cx={480} cy={270} r={13} fill="#0f172a" stroke="#ef4444" strokeWidth={2} />
                      <text x={480} y={275} textAnchor="middle" fill="#ef4444">4</text>
                      <text x={430} y={275} textAnchor="end" fill="#ef4444" fontSize={9}>Contre-braquer</text>
                    </g>
                  </>
                )}
              </g>
            )}

            {stepId === 'M9' && (
              <g>
                <rect x={0} y={200} width={800} height={300} fill="url(#mv-road)" />
                <line x1={0} y1={350} x2={800} y2={350} stroke="#fbbf24" strokeWidth={3} strokeDasharray="20 14" opacity={0.75} />
                {mode === 'demi3temps' ? (
                  <>
                    {/* Trajectoire en 3 temps */}
                    <path d="M 200 400 Q 400 400 550 260 Q 620 220 700 220" stroke="#22c55e" strokeWidth={4} fill="none" strokeDasharray="12 8" opacity={0.75} />
                    <path d="M 700 220 Q 500 220 400 380" stroke="#f59e0b" strokeWidth={4} fill="none" strokeDasharray="12 8" opacity={0.75} />
                    <path d="M 400 380 Q 300 380 100 300" stroke="#8b5cf6" strokeWidth={4} fill="none" strokeDasharray="12 8" opacity={0.75} />
                    <text x={550} y={195} fill="#22c55e" fontSize={13} fontWeight="bold">1️⃣ Avant + braq G</text>
                    <text x={550} y={255} fill="#f59e0b" fontSize={13} fontWeight="bold">2️⃣ Recul + braq D</text>
                    <text x={200} y={280} fill="#8b5cf6" fontSize={13} fontWeight="bold">3️⃣ Redresser</text>
                    {/* Voiture EGO */}
                    <g transform="translate(200 400) rotate(90)">
                      <rect x={-18} y={-32} width={36} height={64} rx={12} fill="#dc2626" stroke="#0b1120" strokeWidth={2.5} />
                      <text y={4} textAnchor="middle" fontSize={10} fontWeight="900" fill="#fff">MOI</text>
                    </g>
                  </>
                ) : (
                  <>
                    {/* Sortie privée à droite */}
                    <rect x={550} y={80} width={100} height={120} fill="url(#mv-grass)" stroke="#334155" strokeWidth={2} />
                    <rect x={560} y={90} width={80} height={100} fill="#0f172a" stroke="#334155" strokeWidth={1.5} />
                    <text x={600} y={140} textAnchor="middle" fill="#94a3b8" fontSize={11} fontFamily="monospace">Entrée</text>
                    <text x={600} y={155} textAnchor="middle" fill="#94a3b8" fontSize={11} fontFamily="monospace">privée</text>
                    <path d="M 200 400 L 550 250 L 600 200" stroke="#0ea5e9" strokeWidth={4} fill="none" strokeDasharray="12 8" opacity={0.75} />
                    <path d="M 600 200 L 400 380" stroke="#22c55e" strokeWidth={4} fill="none" strokeDasharray="12 8" opacity={0.75} />
                    <text x={350} y={310} fill="#0ea5e9" fontSize={13} fontWeight="bold">1️⃣ Avant dans l&apos;allée</text>
                    <text x={400} y={430} fill="#22c55e" fontSize={13} fontWeight="bold">2️⃣ Ressortir en MA</text>
                    <g transform="translate(200 400) rotate(90)">
                      <rect x={-18} y={-32} width={36} height={64} rx={12} fill="#dc2626" stroke="#0b1120" strokeWidth={2.5} />
                      <text y={4} textAnchor="middle" fontSize={10} fontWeight="900" fill="#fff">MOI</text>
                    </g>
                  </>
                )}
              </g>
            )}

            {stepId === 'M11' && (() => {
              // Vue de côté : pente montante vers la droite (~20°).
              // Ligne de pente: de (0, 620) à (800, 320)  → tan θ = 300/800 = 0.375 → θ ≈ 20.55°
              const slopeAngleDeg = -20.55; // rotation pour aligner la voiture sur la pente
              const carStart = { x: 300, y: 620 - 300 * 0.375 }; // (300, 507.5)
              const carSuccessEnd = { x: 640, y: 620 - 640 * 0.375 }; // sommet (640, 380)
              const carFailEnd = { x: 60, y: 620 - 60 * 0.375 };      // pied de pente (60, 597.5)

              // Sous-mode selon frein à main
              const withHandbrake = mode === 'freinamain';

              return (
                <g>
                  {/* Ciel */}
                  <rect x={0} y={0} width={800} height={700} fill="url(#mv-grass)" opacity={0.25} />
                  <rect x={0} y={0} width={800} height={420} fill="#0f2537" opacity={0.55} />
                  {/* Soleil */}
                  <circle cx={680} cy={90} r={26} fill="#fef3c7" opacity={0.85} />
                  <circle cx={680} cy={90} r={38} fill="#fbbf24" opacity={0.15} />

                  {/* PENTE : polygone rempli (montagne / route) */}
                  <path d="M 0 620 L 800 320 L 800 700 L 0 700 Z" fill="url(#mv-grass)" />
                  {/* Route bitume (bande épaisse le long de la pente) */}
                  <path d="M 0 640 L 800 340 L 800 380 L 0 680 Z" fill="url(#mv-road)" />
                  {/* Marquage médian jaune sur la route inclinée */}
                  <line x1={0} y1={660} x2={800} y2={360} stroke="#fbbf24" strokeWidth={3} strokeDasharray="22 15" opacity={0.75} />
                  {/* Marquage bord haut de route (blanc) */}
                  <line x1={0} y1={640} x2={800} y2={340} stroke="#e2e8f0" strokeWidth={2} opacity={0.55} />
                  <line x1={0} y1={680} x2={800} y2={380} stroke="#e2e8f0" strokeWidth={2} opacity={0.55} />

                  {/* Panneau pente (indicateur pourcentage) */}
                  <g transform="translate(90 210)">
                    <polygon points="0,-40 40,-40 40,0 0,0" fill="#fef3c7" stroke="#dc2626" strokeWidth={3} transform="rotate(-20.55)" />
                    <text x={18} y={-14} textAnchor="middle" fill="#0b1120" fontSize={13} fontWeight="900" fontFamily="monospace" transform="rotate(-20.55)">15%</text>
                  </g>

                  {/* Repère « départ » et « sommet » */}
                  <g>
                    <circle cx={carStart.x} cy={carStart.y + 40} r={5} fill="#fbbf24" />
                    <text x={carStart.x} y={carStart.y + 62} textAnchor="middle" fill="#fbbf24" fontSize={10} fontFamily="monospace" fontWeight="bold">DÉPART</text>
                    <circle cx={carSuccessEnd.x} cy={carSuccessEnd.y + 40} r={5} fill="#22c55e" />
                    <text x={carSuccessEnd.x} y={carSuccessEnd.y + 62} textAnchor="middle" fill="#22c55e" fontSize={10} fontFamily="monospace" fontWeight="bold">SOMMET ✓</text>
                    <circle cx={carFailEnd.x + 20} cy={carFailEnd.y + 40} r={5} fill="#dc2626" />
                    <text x={carFailEnd.x + 20} y={carFailEnd.y + 62} textAnchor="middle" fill="#dc2626" fontSize={10} fontFamily="monospace" fontWeight="bold">CHUTE ✗</text>
                  </g>

                  {/* VOITURE — vue de côté, animée selon sim */}
                  <g key={`car-${sim}-${mode}`}>
                    {sim === 'success' && (
                      <animateTransform
                        attributeName="transform"
                        type="translate"
                        values={`${carStart.x},${carStart.y}; ${carSuccessEnd.x},${carSuccessEnd.y}`}
                        keyTimes="0; 1"
                        dur="4.5s"
                        fill="freeze"
                        calcMode="spline"
                        keySplines="0.25 0.1 0.4 1"
                      />
                    )}
                    {sim === 'fail' && (
                      <animateTransform
                        attributeName="transform"
                        type="translate"
                        values={`${carStart.x},${carStart.y}; ${carStart.x - 10},${carStart.y + 4}; ${carFailEnd.x},${carFailEnd.y}`}
                        keyTimes="0; 0.15; 1"
                        dur="3.8s"
                        fill="freeze"
                        calcMode="spline"
                        keySplines="0.6 0 0.8 0.4; 0.2 0.6 0.8 1"
                      />
                    )}
                    {sim === 'idle' && (
                      <animateTransform
                        attributeName="transform"
                        type="translate"
                        values={`${carStart.x},${carStart.y}`}
                        dur="0.1s"
                        fill="freeze"
                      />
                    )}

                    {/* Groupe interne pour rotation sur la pente */}
                    <g transform={`rotate(${slopeAngleDeg})`}>
                      {/* Ombre */}
                      <ellipse cx={0} cy={30} rx={58} ry={5} fill="#000" opacity={0.4} />
                      {/* Roues */}
                      <circle cx={-35} cy={18} r={15} fill="#18181b" stroke="#0b1120" strokeWidth={2} />
                      <circle cx={-35} cy={18} r={7} fill="#71717a" />
                      <line x1={-35} y1={11} x2={-35} y2={25} stroke="#3f3f46" strokeWidth={1.5} />
                      <line x1={-42} y1={18} x2={-28} y2={18} stroke="#3f3f46" strokeWidth={1.5} />
                      <circle cx={35} cy={18} r={15} fill="#18181b" stroke="#0b1120" strokeWidth={2} />
                      <circle cx={35} cy={18} r={7} fill="#71717a" />
                      <line x1={35} y1={11} x2={35} y2={25} stroke="#3f3f46" strokeWidth={1.5} />
                      <line x1={28} y1={18} x2={42} y2={18} stroke="#3f3f46" strokeWidth={1.5} />
                      {/* Corps voiture (silhouette latérale) */}
                      <path d="M -54 15 L -50 -4 L -30 -20 L 18 -24 L 40 -14 L 54 -4 L 54 15 Z" fill="#dc2626" stroke="#0b1120" strokeWidth={2.2} />
                      {/* Ligne de séparation vitre / carrosserie */}
                      <path d="M -46 -4 L 48 -4" stroke="#0b1120" strokeWidth={1.5} opacity={0.55} />
                      {/* Pare-brise avant */}
                      <path d="M 18 -24 L 40 -14 L 40 -6 L 20 -6 Z" fill="#0ea5e9" opacity={0.55} />
                      {/* Vitre latérale */}
                      <path d="M -28 -20 L 16 -24 L 16 -6 L -28 -6 Z" fill="#0ea5e9" opacity={0.55} />
                      {/* Montant central */}
                      <line x1={-4} y1={-22} x2={-4} y2={-5} stroke="#0b1120" strokeWidth={2} />
                      {/* Poignée + rétro */}
                      <rect x={-16} y={-2} width={16} height={2.5} rx={1} fill="#7f1d1d" />
                      <path d="M 40 -12 L 47 -10 L 45 -6 L 40 -8 Z" fill="#0b1120" />
                      {/* Phare avant */}
                      <circle cx={53} cy={-4} r={3.5} fill="#fef3c7" />
                      {/* Feu arrière */}
                      <rect x={-55} y={-6} width={4} height={6} fill={sim === 'fail' ? '#f87171' : '#7f1d1d'} />
                      {/* Fumée d'échappement (visible pendant simulation success) */}
                      {sim === 'success' && (
                        <g>
                          <circle cx={-58} cy={12} r={4} fill="#94a3b8" opacity={0.6}>
                            <animate attributeName="opacity" values="0.6;0;0.6" dur="0.6s" repeatCount="indefinite" />
                            <animate attributeName="cx" values="-58;-72;-58" dur="0.6s" repeatCount="indefinite" />
                          </circle>
                          <circle cx={-66} cy={9} r={3} fill="#cbd5e1" opacity={0.5}>
                            <animate attributeName="opacity" values="0.5;0;0.5" dur="0.7s" repeatCount="indefinite" />
                          </circle>
                        </g>
                      )}
                      {/* Icône frein à main (visible quand FA activé) */}
                      {withHandbrake && sim !== 'success' && (
                        <g transform="translate(0 -32)">
                          <rect x={-16} y={-10} width={32} height={16} rx={6} fill="#0b1120" stroke="#22c55e" strokeWidth={1.5} />
                          <text y={2} textAnchor="middle" fill="#22c55e" fontSize={10} fontWeight="900" fontFamily="monospace">🅿️ FA</text>
                        </g>
                      )}
                      {/* Croix rouge si simulation d'erreur */}
                      {sim === 'fail' && (
                        <g transform="translate(0 -40)">
                          <circle r={15} fill="#dc2626" />
                          <text y={5} textAnchor="middle" fill="#fff" fontSize={18} fontWeight="900">!</text>
                        </g>
                      )}
                      {/* Étoile succès */}
                      {sim === 'success' && (
                        <g transform="translate(0 -42)">
                          <circle r={15} fill="#22c55e" />
                          <text y={6} textAnchor="middle" fill="#fff" fontSize={18} fontWeight="900">✓</text>
                        </g>
                      )}
                    </g>
                  </g>

                  {/* Panneau ÉTAT — coin haut gauche */}
                  <g transform="translate(30 40)">
                    <rect width={270} height={160} rx={10} fill="#0f172a" stroke="#334155" strokeWidth={2} opacity={0.95} />
                    <text x={135} y={22} textAnchor="middle" fill="#e2e8f0" fontSize={12} fontFamily="monospace" fontWeight="bold">
                      {withHandbrake ? 'MODE FREIN À MAIN' : 'MODE PIED-PIED (avancé)'}
                    </text>
                    <line x1={12} y1={30} x2={258} y2={30} stroke="#334155" strokeWidth={1} />

                    {/* Frein à main */}
                    <g transform="translate(16 46)">
                      <text fill="#94a3b8" fontSize={10} fontFamily="monospace">FREIN À MAIN</text>
                      <rect x={130} y={-10} width={100} height={16} rx={8} fill={withHandbrake ? '#052e16' : '#450a0a'} stroke={withHandbrake ? '#22c55e' : '#dc2626'} strokeWidth={1.5} />
                      <text x={180} y={2} textAnchor="middle" fill={withHandbrake ? '#22c55e' : '#f87171'} fontSize={10} fontWeight="900">
                        {withHandbrake ? 'SERRÉ' : 'RELÂCHÉ'}
                      </text>
                    </g>

                    {/* Embrayage — point de patinage */}
                    <g transform="translate(16 74)">
                      <text fill="#94a3b8" fontSize={10} fontFamily="monospace">EMBRAYAGE</text>
                      <rect x={130} y={-10} width={100} height={16} rx={8} fill="#0b1120" stroke="#f59e0b" strokeWidth={1.5} />
                      <rect x={132} y={-8} width={sim === 'idle' ? 40 : sim === 'success' ? 68 : 25} height={12} rx={6} fill="#f59e0b" style={{ transition: 'width 800ms' }} />
                      <text x={180} y={2} textAnchor="middle" fill="#fff" fontSize={9} fontWeight="900">
                        {sim === 'success' ? 'PATINAGE ✓' : sim === 'fail' ? 'trop tôt' : 'appui'}
                      </text>
                    </g>

                    {/* Gaz / moteur RPM */}
                    <g transform="translate(16 102)">
                      <text fill="#94a3b8" fontSize={10} fontFamily="monospace">MOTEUR</text>
                      <rect x={130} y={-10} width={100} height={16} rx={8} fill="#0b1120" stroke="#22c55e" strokeWidth={1.5} />
                      <rect x={132} y={-8} width={sim === 'success' ? 78 : sim === 'fail' ? 15 : 40} height={12} rx={6} fill="#22c55e" style={{ transition: 'width 800ms' }} />
                      <text x={180} y={2} textAnchor="middle" fill="#fff" fontSize={9} fontWeight="900">
                        {sim === 'success' ? '1500 tr/min' : sim === 'fail' ? 'CALÉ' : '800 tr/min'}
                      </text>
                    </g>

                    {/* Flèche de force résultante */}
                    <g transform="translate(16 130)">
                      <text fill="#94a3b8" fontSize={10} fontFamily="monospace">RÉSULTAT</text>
                      <text x={130} y={4} fill={sim === 'success' ? '#22c55e' : sim === 'fail' ? '#dc2626' : '#94a3b8'} fontSize={11} fontWeight="900">
                        {sim === 'success' ? '↗ La voiture MONTE' : sim === 'fail' ? '↙ La voiture RECULE' : 'En attente…'}
                      </text>
                    </g>
                  </g>

                  {/* Boutons de simulation — coin haut droit */}
                  <g transform="translate(500 40)">
                    <rect width={270} height={100} rx={10} fill="#0f172a" stroke="#334155" strokeWidth={2} opacity={0.95} />
                    <text x={135} y={22} textAnchor="middle" fill="#e2e8f0" fontSize={12} fontFamily="monospace" fontWeight="bold">
                      LANCER LA SIMULATION
                    </text>
                    <line x1={12} y1={30} x2={258} y2={30} stroke="#334155" strokeWidth={1} />
                    {/* Boutons cliquables */}
                    <g style={{ cursor: 'pointer' }} onClick={() => runSim('success')} data-testid="m11-run-success">
                      <rect x={12} y={42} width={120} height={44} rx={10} fill="#052e16" stroke="#22c55e" strokeWidth={2} />
                      <text x={72} y={62} textAnchor="middle" fill="#22c55e" fontSize={11} fontWeight="900">✓ BONNE</text>
                      <text x={72} y={78} textAnchor="middle" fill="#22c55e" fontSize={11} fontWeight="900">PROCÉDURE</text>
                    </g>
                    <g style={{ cursor: 'pointer' }} onClick={() => runSim('fail')} data-testid="m11-run-fail">
                      <rect x={140} y={42} width={120} height={44} rx={10} fill="#450a0a" stroke="#dc2626" strokeWidth={2} />
                      <text x={200} y={62} textAnchor="middle" fill="#f87171" fontSize={11} fontWeight="900">✗ SIMULER</text>
                      <text x={200} y={78} textAnchor="middle" fill="#f87171" fontSize={11} fontWeight="900">ERREUR</text>
                    </g>
                  </g>

                  {/* Herbe / arbres décor */}
                  <g opacity={0.7}>
                    <circle cx={220} cy={550} r={16} fill="#365828" />
                    <rect x={218} y={550} width={4} height={18} fill="#3f3f46" />
                    <circle cx={560} cy={400} r={16} fill="#365828" />
                    <rect x={558} y={400} width={4} height={18} fill="#3f3f46" />
                  </g>
                </g>
              );
            })()}

            {/* Bandeau bas */}
            <g transform="translate(0, 655)">
              <rect width={800} height={45} fill="#000" opacity={0.65} />
              <text x={20} y={28} fill="#fff" fontSize={13} fontWeight="bold">{cfg.title}</text>
              <text x={20} y={42} fill="#94a3b8" fontSize={11}>REMC {cfg.remc}</text>
              <circle cx={770} cy={22} r={16} fill="#dc2626" />
              <text x={770} y={27} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid={`${stepId.toLowerCase()}-panel`}>
          <div className="rounded-xl border-2 p-3.5" style={{ borderColor: currentMode.color, background: currentMode.color + '18' }}>
            <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: currentMode.color }}>Mode</div>
            <div className="mt-1 font-serif-editorial text-white text-[19px] leading-tight">{current.titre}</div>
          </div>

          <div className="mt-3 rounded-lg border border-cyan-500/30 bg-cyan-950/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Description</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{current.description}</p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-purple-500/40 bg-purple-950/15 p-3">
            <div className="text-purple-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-1.5">📋 Étapes</div>
            <ol className="text-white/85 text-[12px] leading-relaxed list-decimal ml-4 space-y-0.5">
              {current.etapes.map((e, i) => <li key={i}>{e}</li>)}
            </ol>
          </div>

          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">⚠ Piège</div>
              <p className="mt-1 text-white/85 text-[12px] leading-relaxed">{current.piege}</p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-3 flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Bon geste</div>
              <p className="mt-1 text-white/85 text-[12px] leading-relaxed">{current.geste}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
